import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { TrendingUp, Loader2, Paperclip, X, CreditCard } from "lucide-react";
import { PROF_T, LEVEL_IDS } from "../content/professions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHUNK = 4 * 1024 * 1024;
const inp = "w-full bg-black border border-white/15 px-3 py-2.5 text-sm text-white focus:border-[#D4AF37] outline-none";
const TT = {
  sk: { title: "Žiadosť o vyššiu úroveň", hint: "Získali ste nové skúsenosti? Vyberte profesiu, novú úroveň a priložte nové podklady (certifikáty, referencie, fotky práce). Poplatok za posúdenie je 50 €.",
    prof: "Profesia", level: "Nová úroveň", note: "Krátke zdôvodnenie (nepovinné)", files: "Nové podklady (povinné)", add: "Priložiť súbor",
    pay: "Odoslať a zaplatiť 50 €", none: "Najprv vyplňte žiadosť o certifikáciu s profesiami.", top: "Vo všetkých profesiách máte najvyššiu úroveň alebo žiadosť v posudzovaní.",
    mine: "Moje žiadosti", paid: "Platba prijatá – žiadosť posudzujeme.", verifying: "Overujem platbu…",
    st: { pending: "Posudzuje sa", approved: "Schválená", rejected: "Zamietnutá" } },
  en: { title: "Request a higher level", hint: "Gained new experience? Choose the profession, the new level and attach new evidence (certificates, references, photos of work). The review fee is €50.",
    prof: "Profession", level: "New level", note: "Short justification (optional)", files: "New evidence (required)", add: "Attach file",
    pay: "Submit and pay €50", none: "First fill in the certification application with your professions.", top: "You already have the highest level or a request under review in every profession.",
    mine: "My requests", paid: "Payment received – we are reviewing your request.", verifying: "Verifying payment…",
    st: { pending: "Under review", approved: "Approved", rejected: "Rejected" } },
  de: { title: "Antrag auf höhere Stufe", hint: "Neue Erfahrungen gesammelt? Wählen Sie den Beruf, die neue Stufe und fügen Sie neue Nachweise bei (Zertifikate, Referenzen, Fotos der Arbeit). Die Prüfgebühr beträgt 50 €.",
    prof: "Beruf", level: "Neue Stufe", note: "Kurze Begründung (optional)", files: "Neue Nachweise (Pflicht)", add: "Datei anhängen",
    pay: "Absenden und 50 € zahlen", none: "Füllen Sie zuerst den Zertifizierungsantrag mit Ihren Berufen aus.", top: "In allen Berufen haben Sie bereits die höchste Stufe oder einen Antrag in Prüfung.",
    mine: "Meine Anträge", paid: "Zahlung erhalten – wir prüfen Ihren Antrag.", verifying: "Zahlung wird geprüft…",
    st: { pending: "In Prüfung", approved: "Genehmigt", rejected: "Abgelehnt" } },
  pl: { title: "Wniosek o wyższy poziom", hint: "Zdobyłeś nowe doświadczenie? Wybierz zawód, nowy poziom i dołącz nowe dokumenty (certyfikaty, referencje, zdjęcia pracy). Opłata za ocenę wynosi 50 €.",
    prof: "Zawód", level: "Nowy poziom", note: "Krótkie uzasadnienie (opcjonalnie)", files: "Nowe dokumenty (wymagane)", add: "Dołącz plik",
    pay: "Wyślij i zapłać 50 €", none: "Najpierw wypełnij wniosek o certyfikację z zawodami.", top: "We wszystkich zawodach masz już najwyższy poziom lub wniosek w ocenie.",
    mine: "Moje wnioski", paid: "Płatność otrzymana – oceniamy wniosek.", verifying: "Weryfikacja płatności…",
    st: { pending: "W ocenie", approved: "Zatwierdzony", rejected: "Odrzucony" } },
};
const RANK = { beginner: 1, experienced: 2, professional: 3 };

export async function uploadFile(file, auth) {
  const { data: b } = await axios.post(`${API}/applicant/upload/begin`, { filename: file.name, content_type: file.type, kind: "document" }, auth());
  for (let s = 0; s < file.size; s += CHUNK) {
    await axios.put(`${API}/applicant/upload/chunk/${b.upload_id}`, file.slice(s, s + CHUNK), { headers: { ...auth().headers, "Content-Type": "application/octet-stream" } });
  }
  const { data } = await axios.post(`${API}/applicant/upload/finish/${b.upload_id}`, {}, auth());
  return data;
}

function MyRequests({ rows, P, T }) {
  if (!rows.length) return null;
  return (
    <div className="mt-6 space-y-2" data-testid="level-requests-list">
      <p className="text-[11px] tracking-[0.2em] uppercase text-[#D4AF37]/80">{T.mine}</p>
      {rows.map((r) => (
        <div key={r.id} className="flex flex-wrap justify-between gap-2 border border-white/10 px-4 py-2.5 text-sm" data-testid={`level-request-${r.id}`}>
          <span className="text-white/80">{P.names[r.profession] || r.profession} → {P.levels[r.new_level]}</span>
          <span className={r.status === "approved" ? "text-emerald-300" : r.status === "rejected" ? "text-red-300" : "text-[#D4AF37]"} data-testid={`level-request-status-${r.id}`}>{T.st[r.status] || r.status}</span>
          {r.admin_note && <p className="w-full text-white/45 text-xs">{r.admin_note}</p>}
        </div>
      ))}
    </div>
  );
}

export default function LevelUpRequest({ lang, auth }) {
  const P = PROF_T[lang] || PROF_T.sk;
  const T = TT[lang] || TT.sk;
  const [d, setD] = useState(null);
  const [prof, setProf] = useState("");
  const [level, setLevel] = useState("");
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const load = useCallback(() => axios.get(`${API}/applicant/level-requests`, auth()).then(({ data }) => setD(data)).catch(() => {}), // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const sid = q.get("session_id");
    if (!sid || !q.get("lvl")) { load(); return; }
    setMsg(T.verifying);
    let n = 0;
    const poll = async () => {
      n += 1;
      try { const { data } = await axios.get(`${API}/applicant/payment/status/${sid}`, auth()); if (data.payment_status === "paid" || n > 6) { window.history.replaceState({}, "", "/ziadost?tab=profile"); setMsg(data.payment_status === "paid" ? T.paid : ""); return load(); } } catch { /* retry */ }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!d) return null;
  const busyProf = new Set(d.requests.filter((r) => r.status === "pending").map((r) => r.profession));
  const options = d.professions.filter((p) => (RANK[d.prof_levels[p]] || 0) < 3 && !busyProf.has(p));
  const higher = LEVEL_IDS.filter((l) => RANK[l] > (RANK[d.prof_levels[prof]] || 0));
  const add = async (f) => {
    if (!f) return;
    setBusy("file"); setMsg("");
    try { const r = await uploadFile(f, auth); setFiles((x) => [...x, r]); } catch (e) { setMsg(e?.response?.data?.detail || "Error"); } finally { setBusy(""); }
  };
  const submit = async () => {
    setBusy("pay"); setMsg("");
    try {
      const { data } = await axios.post(`${API}/applicant/level-requests`, { profession: prof, new_level: level, note, file_ids: files.map((f) => f.id), origin_url: window.location.origin }, auth());
      window.location.href = data.url;
    } catch (e) { setMsg(e?.response?.data?.detail || "Error"); setBusy(""); }
  };
  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-6 mb-6" data-testid="level-up-request">
      <h3 className="font-display text-xl flex items-center gap-2 mb-1"><TrendingUp size={18} className="text-[#D4AF37]" /> {T.title}</h3>
      <p className="text-white/40 text-sm mb-4">{T.hint}</p>
      {!d.professions.length ? <p className="text-white/50 text-sm" data-testid="level-up-none">{T.none}</p> : !options.length ? <p className="text-white/50 text-sm" data-testid="level-up-top">{T.top}</p> : (
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-white/50 mb-2">{T.prof}</label>
              <select value={prof} onChange={(e) => { setProf(e.target.value); setLevel(""); }} className={inp} data-testid="level-up-prof">
                <option value="">—</option>
                {options.map((p) => <option key={p} value={p}>{(p === "other" ? d.profession_other || P.names.other : P.names[p] || p)}{d.prof_levels[p] ? ` (${P.levels[d.prof_levels[p]]})` : ""}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-white/50 mb-2">{T.level}</label>
              <select value={level} onChange={(e) => setLevel(e.target.value)} disabled={!prof} className={inp} data-testid="level-up-level">
                <option value="">—</option>
                {higher.map((l) => <option key={l} value={l}>{P.levels[l]}</option>)}
              </select>
            </div>
          </div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder={T.note} className={inp} data-testid="level-up-note" />
          <div>
            <p className="text-xs text-white/50 mb-2">{T.files}</p>
            {files.map((f) => (
              <p key={f.id} className="text-sm text-white/70 flex items-center gap-2" data-testid={`level-up-file-${f.id}`}><Paperclip size={13} /> {f.original_filename}
                <button onClick={() => setFiles((x) => x.filter((y) => y.id !== f.id))} className="text-white/40 hover:text-white" data-testid={`level-up-file-remove-${f.id}`}><X size={13} /></button></p>
            ))}
            <label className="mt-2 inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-sm text-white/70 hover:border-[#D4AF37]/60 cursor-pointer">
              {busy === "file" ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />} {T.add}
              <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => { add(e.target.files[0]); e.target.value = ""; }} data-testid="level-up-file-input" />
            </label>
          </div>
          <button onClick={submit} disabled={!prof || !level || !files.length || !!busy} data-testid="level-up-submit"
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-5 py-2.5 text-sm hover:bg-[#F2E3C6] disabled:opacity-50">
            {busy === "pay" ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />} {T.pay}
          </button>
        </div>
      )}
      {msg && <p className="text-[#D4AF37] text-sm mt-3" data-testid="level-up-msg">{msg}</p>}
      <MyRequests rows={d.requests} P={P} T={T} />
    </div>
  );
}
